import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';

export default function UserGalleryPage() {
  const { user, userId, isAuthReady, userProfile } = useContext(AuthContext);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!isAuthReady) return;

    if (!user || !userId) {
      setMessage("Please log in to view user images.");
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Get gallery photos from userProfile context
    if (userProfile && userProfile.galleryPhotoUrls) {
      setImages(userProfile.galleryPhotoUrls);
    } else {
      setImages([]);
    }
    
    setLoading(false);
  }, [user, userId, isAuthReady, userProfile]);

  if (!isAuthReady) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}>
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent shadow-lg" 
               style={{ borderColor: 'hsl(43, 74%, 52%)', borderTopColor: 'transparent' }}></div>
          <p className="text-xl text-white font-medium">Loading images...</p>
        </div>
      </div>
    );
  }

  if (message) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4" style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}>
        <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full text-center transform hover:scale-105 transition-all duration-300">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center shadow-lg" 
               style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <p className="text-xl font-bold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>{message}</p>
          <p className="text-gray-600">Sign in to explore the gallery</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}>
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent shadow-lg" 
               style={{ borderColor: 'hsl(43, 74%, 52%)', borderTopColor: 'transparent' }}></div>
          <p className="text-xl text-white font-medium">Loading images...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-12 px-4" style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}>
      <div className="container mx-auto max-w-7xl">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-block mb-4">
            <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center shadow-xl transform hover:rotate-6 transition-all duration-300" 
                 style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <h1 className="text-5xl font-extrabold text-white mb-4 tracking-tight">
            My Image Gallery
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Your personal photo collection
          </p>
        </div>

        {/* Gallery Grid */}
        {images.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center transform hover:scale-105 transition-all duration-300">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center" 
                 style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-2xl font-bold mb-3" style={{ color: 'hsl(221, 45%, 18%)' }}>
              No Images Available
            </p>
            <p className="text-gray-600 text-lg">
              Upload photos in your profile to see them here!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {images.map((imageUrl, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl shadow-lg overflow-hidden transform hover:scale-105 hover:shadow-2xl transition-all duration-300 group"
              >
                {/* Image Container */}
                <div className="relative overflow-hidden bg-gray-100" style={{ paddingBottom: '75%' }}>
                  <img
                    src={imageUrl}
                    alt={`Gallery Image ${index + 1}`}
                    className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => { 
                      e.target.onerror = null; 
                      e.target.src="https://placehold.co/800x600/cccccc/666666?text=Image+Error"; 
                    }}
                  />
                  {/* Gradient Overlay on Hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
                       style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)' }}>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <button 
                        onClick={() => window.open(imageUrl, '_blank')}
                        className="w-full py-2 px-4 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 hover:shadow-xl"
                        style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
                      >
                        View Full Size
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Card Footer */}
                <div className="p-4 border-t-2" style={{ borderColor: 'hsl(43, 74%, 52%, 0.1)' }}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold" style={{ color: 'hsl(221, 45%, 18%)' }}>
                      Image {index + 1}
                    </p>
                    <div className="flex items-center space-x-2">
                      <button className="p-2 rounded-lg transition-all duration-200 hover:bg-gray-100" title="Like">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      </button>
                      <button className="p-2 rounded-lg transition-all duration-200 hover:bg-gray-100" title="Share">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Section */}
        {images.length > 0 && (
          <div className="mt-12 bg-white rounded-2xl shadow-xl p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="p-4">
                <p className="text-4xl font-bold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
                  {images.length}
                </p>
                <p className="text-gray-600 font-medium">Total Images</p>
              </div>
              <div className="p-4 border-x border-gray-200">
                <p className="text-4xl font-bold mb-2" style={{ color: 'hsl(43, 74%, 52%)' }}>
                  {Math.floor(images.length * 1.5)}
                </p>
                <p className="text-gray-600 font-medium">Total Views</p>
              </div>
              <div className="p-4">
                <p className="text-4xl font-bold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
                  {Math.floor(images.length * 0.8)}
                </p>
                <p className="text-gray-600 font-medium">Total Likes</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}